import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Card, Table, DatePicker, Segmented, Modal, Tag, Statistic, Row, Col, Empty, Button, Dropdown, Checkbox } from "antd";
import { TrophyOutlined, DownloadOutlined, FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const { RangePicker } = DatePicker;

// Half-yearly has no native antd DatePicker granularity, so it's driven off
// a plain "month" anchor — whichever half of the year that month falls in
// is the selected period. Keeps the picker UI simple (one control) instead
// of a bespoke half/year pair of dropdowns.
const halfYearRange = (anchor) => {
  const year = anchor.year();
  const isFirstHalf = anchor.month() < 6; // Jan-Jun vs Jul-Dec
  const from = moment([year, isFirstHalf ? 0 : 6, 1]).startOf("day");
  const to = isFirstHalf ? moment([year, 5, 30]).endOf("day") : moment([year, 11, 31]).endOf("day");
  return { from, to };
};

// Which org level a role reports "up" into, and what its own team members
// are called — drives both the top-level report and every nested
// drill-down frame's column header/labels.
const ROLE_LABEL = { bm: "Brand Manager", bs: "Brand Strategist", bh: "Brand Head" };

function RenewalRate() {
  const { request } = useRequest();
  const [periodType, setPeriodType] = useState("quarter"); // month | quarter | half | year | custom
  const [anchor, setAnchor] = useState(moment());
  const [customRange, setCustomRange] = useState([moment().startOf("month"), moment().endOf("month")]);
  // Which level(s) the top-level report/chart show — multi-select, so Admin
  // can view e.g. Brand Managers and Brand Strategists side by side in one
  // table. Always at least one selected; toggling the last checked option
  // off is a no-op (see toggleOrgLevel) rather than leaving the view empty.
  const [orgLevels, setOrgLevels] = useState(["bm"]);
  const toggleOrgLevel = (level) => {
    setOrgLevels((prev) => {
      if (prev.includes(level)) {
        return prev.length > 1 ? prev.filter((l) => l !== level) : prev;
      }
      // Keep a stable bm -> bs -> bh order regardless of click order, so the
      // table/chart/export don't reshuffle based on which box was ticked last.
      return ["bm", "bs", "bh"].filter((l) => prev.includes(l) || l === level);
    });
  };
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);
  // Nested drill-down: a stack of frames so BH -> BS -> BM -> clients can go
  // as deep as the data allows, each frame rendered in the same Modal with
  // a "Back" step rather than opening modal-on-modal. A "team" frame shows
  // the same renewed/lost/pending table as the top level (scoped to that
  // one staff member's own team); a "clients" frame is the original
  // client-level table (leaf of the drill).
  const [drillStack, setDrillStack] = useState([]); // [{ kind: "team"|"clients", role, staffId, name, rows, loading }]
  const [exportingConsolidated, setExportingConsolidated] = useState(false);
  const [exportingKey, setExportingKey] = useState(null); // staffId of the row being exported

  const range = useCallback(() => {
    if (periodType === "half") {
      const { from, to } = halfYearRange(anchor);
      return { from: from.toISOString(), to: to.toISOString() };
    }
    if (periodType === "custom") {
      return {
        from: customRange[0].clone().startOf("day").toISOString(),
        to: customRange[1].clone().endOf("day").toISOString(),
      };
    }
    return {
      from: anchor.clone().startOf(periodType).toISOString(),
      to: anchor.clone().endOf(periodType).toISOString(),
    };
  }, [periodType, anchor, customRange]);

  const periodLabel = () => {
    if (periodType === "custom") {
      return `${customRange[0].format("MMM D, YYYY")} – ${customRange[1].format("MMM D, YYYY")}`;
    }
    if (periodType === "half") {
      const { from, to } = halfYearRange(anchor);
      return `${from.format("MMM YYYY")} – ${to.format("MMM YYYY")}`;
    }
    if (periodType === "quarter") return `Q${anchor.quarter()} ${anchor.format("YYYY")}`;
    if (periodType === "year") return anchor.format("YYYY");
    return anchor.format("MMMM YYYY");
  };

  const fetchReport = useCallback(() => {
    setLoading(true);
    const { from, to } = range();
    request({
      url: `${apiPath.brandRenewalRate}?from=${from}&to=${to}&role=${orgLevels.join(",")}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setReport(data?.data?.report || []);
      },
      onError: (err) => {
        setLoading(false);
        ShowToast(err?.response?.data?.message || "Failed to load renewal rate report", Severty.ERROR);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, orgLevels]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Pushes a new drill-down frame onto the stack and fetches its rows.
  // kind "clients" = the original leaf (a BM's own client list). kind
  // "team" = a BS's or BH's own team, rendered as the same renewed/lost/
  // pending table the top level uses, scoped down one org level.
  const pushDrill = (kind, role, staffId, name) => {
    const frame = { kind, role, staffId, name, rows: [], loading: true };
    setDrillStack((prev) => [...prev, frame]);
    const { from, to } = range();
    const url = kind === "clients"
      ? `${apiPath.brandRenewalRateDetail(staffId)}?from=${from}&to=${to}`
      : `${apiPath.brandRenewalRateTeam(staffId)}?from=${from}&to=${to}&role=${role}`;
    request({
      url,
      method: "GET",
      onSuccess: (data) => {
        const rows = kind === "clients" ? (data?.data?.rows || []) : (data?.data?.rows || []);
        setDrillStack((prev) => prev.map((f, i) => (i === prev.length - 1 ? { ...f, rows, loading: false } : f)));
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.message || "Failed to load detail", Severty.ERROR);
        setDrillStack((prev) => prev.map((f, i) => (i === prev.length - 1 ? { ...f, loading: false } : f)));
      },
    });
  };

  // Top-level row click: at BM level this has always meant "show me the
  // clients"; at BS/BH level it means "show me my team" (their BMs, or
  // their BS's) — one level down, not straight to clients. Each row now
  // carries its own `role` (from the possibly-mixed multi-level report), so
  // this reads the row itself rather than a single page-wide level.
  const openDetail = (record) => {
    setDrillStack([]); // fresh drill path each time a top-level row is opened
    if (record.role === "bm") {
      pushDrill("clients", "bm", record.staffId, record.name);
    } else {
      pushDrill("team", record.role, record.staffId, record.name);
    }
  };

  // A row inside a "team" frame drills one level further: a BS row (role
  // "bm" on the row itself) goes to that BM's clients; a BS-under-BH row
  // (role "bs") goes to that BS's own team.
  const drillIntoRow = (row) => {
    if (row.role === "bm") pushDrill("clients", "bm", row.staffId, row.name);
    else pushDrill("team", "bs", row.staffId, row.name);
  };

  const activeFrame = drillStack[drillStack.length - 1] || null;
  const closeDrill = () => setDrillStack([]);
  const popDrill = () => setDrillStack((prev) => prev.slice(0, -1));

  const MIME_BY_FORMAT = {
    excel: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    pdf: "application/pdf",
  };

  const downloadBlob = async (url, filename, format, onDone) => {
    try {
      const token = localStorage.getItem("admin_token") || "";
      const response = await axios.get(url, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([response.data], { type: MIME_BY_FORMAT[format] });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      ShowToast("Failed to export KPI report", Severty.ERROR);
    } finally {
      onDone();
    }
  };

  const exportConsolidated = (format) => {
    setExportingConsolidated(true);
    const { from, to } = range();
    const ext = format === "pdf" ? "pdf" : "xlsx";
    const path = format === "pdf" ? apiPath.brandRenewalRateExportPdf : apiPath.brandRenewalRateExport;
    downloadBlob(
      `${apiPath.baseURL}${path}?from=${from}&to=${to}&role=${orgLevels.join(",")}`,
      `KPI_Report_Renewal_Rate_${orgLevels.join("-").toUpperCase()}_${anchor.format("YYYY-MM-DD")}_${periodType}.${ext}`,
      format,
      () => setExportingConsolidated(false)
    );
  };

  // Works for a BM row (per-client export, unchanged) as well as a BS or BH
  // row — the backend switches to a team-breakdown export (their BMs, or
  // their BS's) based on this same `role` param.
  const exportForBM = (record, format) => {
    setExportingKey(record.staffId);
    const { from, to } = range();
    const ext = format === "pdf" ? "pdf" : "xlsx";
    const path = format === "pdf"
      ? apiPath.brandRenewalRateBMExportPdf(record.staffId)
      : apiPath.brandRenewalRateBMExport(record.staffId);
    downloadBlob(
      `${apiPath.baseURL}${path}?from=${from}&to=${to}&role=${record.role}`,
      `KPI_Report_${record.name.replace(/\s+/g, "_")}_${anchor.format("YYYY-MM-DD")}_${periodType}.${ext}`,
      format,
      () => setExportingKey(null)
    );
  };

  // Only rows with at least one resolved (renewed or lost) contract in the
  // period are meaningful in a "who's doing well" ranking — a row with
  // zero due-for-renewal contracts has nothing to rank, not a 0% rate.
  const resolved = useMemo(
    () => report.filter((r) => r.renewed + r.lost > 0).sort((a, b) => (b.renewalRate ?? -1) - (a.renewalRate ?? -1)),
    [report]
  );

  // The 4 KPI cards + Company Renewal Rate must never just sum every
  // selected level's rows — a BS's numbers ARE a rollup of their own BMs'
  // same contracts, so with BM+BS both selected, naive summing would double
  // -count every renewal/loss. Always base the company-wide totals on the
  // single FINEST selected level (bm if present, else bs, else bh) — the
  // level closest to real contracts — regardless of which other levels are
  // also showing in the table/chart for comparison.
  const finestLevel = orgLevels.includes("bm") ? "bm" : orgLevels.includes("bs") ? "bs" : "bh";
  const companyAvg = useMemo(() => {
    const base = resolved.filter((r) => r.role === finestLevel);
    const totalRenewed = base.reduce((s, r) => s + r.renewed, 0);
    const totalLost = base.reduce((s, r) => s + r.lost, 0);
    const totalRevRetained = base.reduce((s, r) => s + r.revenueRetained, 0);
    const totalRevLost = base.reduce((s, r) => s + r.revenueLost, 0);
    const denom = totalRenewed + totalLost;
    const revDenom = totalRevRetained + totalRevLost;
    return {
      rate: denom > 0 ? Math.round((totalRenewed / denom) * 1000) / 10 : null,
      revenueRate: revDenom > 0 ? Math.round((totalRevRetained / revDenom) * 1000) / 10 : null,
      totalRenewed,
      totalLost,
      totalRevRetained,
      totalRevLost,
    };
  }, [resolved, finestLevel]);

  const chartData = {
    // Disambiguate labels once more than one level is on screen at once
    // (e.g. "Dushyant BS" the BS vs a same-named BM would otherwise look
    // identical on the axis).
    labels: resolved.map((r) => (orgLevels.length > 1 ? `${r.name} (${ROLE_LABEL[r.role]})` : r.name)),
    datasets: [
      {
        label: "Renewal Rate %",
        data: resolved.map((r) => r.renewalRate),
        backgroundColor: resolved.map((r) =>
          r.renewalRate >= 80 ? "#15803d" : r.renewalRate >= 50 ? "#b45309" : "#b91c1c"
        ),
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { x: { min: 0, max: 100, ticks: { callback: (v) => `${v}%` } } },
  };

  const columns = [
    { title: orgLevels.length > 1 ? "Name" : ROLE_LABEL[orgLevels[0]], dataIndex: "name", key: "name" },
    ...(orgLevels.length > 1
      ? [{
          title: "Level",
          dataIndex: "role",
          key: "role",
          filters: orgLevels.map((l) => ({ text: ROLE_LABEL[l], value: l })),
          onFilter: (value, r) => r.role === value,
          render: (v) => <Tag color="blue">{ROLE_LABEL[v]}</Tag>,
        }]
      : []),
    { title: "Due for Renewal", dataIndex: "dueCount", key: "dueCount" },
    { title: "Renewed", dataIndex: "renewed", key: "renewed", render: (v) => <Tag color="green">{v}</Tag> },
    { title: "Lost", dataIndex: "lost", key: "lost", render: (v) => <Tag color="red">{v}</Tag> },
    { title: "Upcoming Contracts", dataIndex: "upcomingCount", key: "upcomingCount", render: (v) => <Tag>{v}</Tag> },
    {
      title: "Renewal Rate",
      dataIndex: "renewalRate",
      key: "renewalRate",
      render: (v) => (v == null ? "-" : `${v}%`),
      sorter: (a, b) => (a.renewalRate ?? -1) - (b.renewalRate ?? -1),
    },
    {
      title: "Revenue Renewal Rate",
      dataIndex: "revenueRenewalRate",
      key: "revenueRenewalRate",
      render: (v) => (v == null ? "-" : `${v}%`),
    },
    {
      title: "Revenue Lost",
      dataIndex: "revenueLost",
      key: "revenueLost",
      render: (v) => (v > 0 ? `$${v.toLocaleString()}` : "-"),
    },
    {
      title: "",
      key: "action",
      width: 230,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <a onClick={() => openDetail(record)}>{record.role === "bm" ? "View clients" : "View team"}</a>
          <Dropdown
            menu={{
              items: [
                { key: "excel", label: "Excel (.xlsx)", icon: <FileExcelOutlined /> },
                { key: "pdf", label: "PDF (.pdf)", icon: <FilePdfOutlined /> },
              ],
              onClick: ({ key }) => exportForBM(record, key),
            }}
          >
            <Button size="small" icon={<DownloadOutlined />} loading={exportingKey === record.staffId}>
              Download
            </Button>
          </Dropdown>
        </div>
      ),
    },
  ];

  const rateCardColor = companyAvg.rate == null ? "#6b7280" : companyAvg.rate >= 80 ? "#15803d" : companyAvg.rate >= 50 ? "#b45309" : "#b91c1c";
  const levelHeading = orgLevels.map((l) => ROLE_LABEL[l]).join(" & ");

  return (
    <>
      <div className="gfx-req-hero gfx-oversight-hero">
        <div className="gfx-req-hero-top">
          <div>
            <div className="gfx-req-hero-eyebrow">Brand Team</div>
            <h2 className="gfx-req-hero-title">KPI Report — Renewal Rate</h2>
          </div>
        </div>
      </div>

      <Card bordered={false} className="criclebox mb-24 gfx-oversight-toolbar-card">
        <div className="gfx-oversight-toolbar" style={{ justifyContent: "space-between", flexWrap: "wrap", rowGap: 12 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <Segmented
              options={[
                { label: "Monthly", value: "month" },
                { label: "Quarterly", value: "quarter" },
                { label: "Half-Yearly", value: "half" },
                { label: "Annually", value: "year" },
                { label: "Custom", value: "custom" },
              ]}
              value={periodType}
              onChange={(v) => setPeriodType(v)}
            />
            {periodType === "custom" ? (
              <RangePicker
                value={customRange}
                onChange={(v) => v && setCustomRange(v)}
                allowClear={false}
              />
            ) : (
              <DatePicker
                picker={periodType === "half" ? "month" : periodType}
                value={anchor}
                allowClear={false}
                onChange={(m) => setAnchor(m || moment())}
              />
            )}
          </div>
          <Dropdown
            menu={{
              items: [
                { key: "excel", label: "Excel (.xlsx)", icon: <FileExcelOutlined /> },
                { key: "pdf", label: "PDF (.pdf)", icon: <FilePdfOutlined /> },
              ],
              onClick: ({ key }) => exportConsolidated(key),
            }}
          >
            <Button type="primary" icon={<DownloadOutlined />} loading={exportingConsolidated}>
              Download KPI Report
            </Button>
          </Dropdown>
        </div>
        <p style={{ color: "#888", marginTop: 12, marginBottom: 0 }}>
          Showing <b>{periodLabel()}</b>. A contract counts as "due for renewal" when its end date falls in this
          window. Renewed = a new contract exists linked back to it. Lost = the end date has passed with no renewal
          (a contract still awaiting a decision isn't counted either way). Upcoming Contracts = the client already
          has a new contract on the books whose start date falls in the period right after this one.
        </p>
      </Card>

      <Row gutter={24} className="mb-24" style={{ marginTop: 24 }}>
        <Col flex="1">
          <Card bordered={false} className="criclebox">
            <Statistic
              title="Company Renewal Rate"
              value={companyAvg.rate == null ? "-" : companyAvg.rate}
              suffix={companyAvg.rate == null ? "" : "%"}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: rateCardColor }}
            />
          </Card>
        </Col>
        <Col flex="1">
          <Card bordered={false} className="criclebox">
            <Statistic title="Clients Renewed" value={companyAvg.totalRenewed} valueStyle={{ color: "#15803d" }} />
          </Card>
        </Col>
        <Col flex="1">
          <Card bordered={false} className="criclebox">
            <Statistic title="Clients Lost" value={companyAvg.totalLost} valueStyle={{ color: "#b91c1c" }} />
          </Card>
        </Col>
        <Col flex="1">
          <Card bordered={false} className="criclebox">
            <Statistic
              title="Revenue Retained"
              value={companyAvg.totalRevRetained}
              prefix="$"
              valueStyle={{ color: "#15803d" }}
            />
          </Card>
        </Col>
        <Col flex="1">
          <Card bordered={false} className="criclebox">
            <Statistic
              title="Revenue Lost"
              value={companyAvg.totalRevLost}
              prefix="$"
              valueStyle={{ color: "#b91c1c" }}
            />
          </Card>
        </Col>
      </Row>

      <Card bordered={false} className="criclebox mb-24">
        <div style={{ color: "#888", marginBottom: 10, fontSize: 13 }}>
          View by (select one or more):
        </div>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <Checkbox checked={orgLevels.includes("bm")} onChange={() => toggleOrgLevel("bm")}>Brand Managers</Checkbox>
          <Checkbox checked={orgLevels.includes("bs")} onChange={() => toggleOrgLevel("bs")}>Brand Strategists</Checkbox>
          <Checkbox checked={orgLevels.includes("bh")} onChange={() => toggleOrgLevel("bh")}>Brand Heads</Checkbox>
        </div>
      </Card>

      <Card bordered={false} className="criclebox mb-24" title={`Renewal Rate by ${levelHeading}`} loading={loading}>
        {resolved.length === 0 ? (
          <Empty description="No contracts were due for renewal in this period" />
        ) : (
          <div style={{ maxWidth: 720, height: Math.max(120, resolved.length * 42) }}>
            <Bar data={chartData} options={chartOptions} />
          </div>
        )}
      </Card>

      <Card bordered={false} className="criclebox tablespace mb-24" title="Details">
        <Table
          rowKey="staffId"
          loading={loading}
          columns={columns}
          dataSource={report}
          pagination={{ pageSize: 10 }}
          scroll={{ x: "max-content" }}
        />
      </Card>

      <Modal
        open={drillStack.length > 0}
        onCancel={closeDrill}
        footer={null}
        title={
          activeFrame
            ? `${drillStack.map((f) => f.name).join(" → ")} — ${
                activeFrame.kind === "clients" ? "Clients Due for Renewal" : "Team Breakdown"
              }`
            : ""
        }
        width={activeFrame?.kind === "team" ? 900 : 720}
      >
        {drillStack.length > 1 && (
          <a onClick={popDrill} style={{ display: "inline-block", marginBottom: 12 }}>
            ← Back to {drillStack[drillStack.length - 2].name}
          </a>
        )}

        {activeFrame?.kind === "clients" ? (
          <Table
            rowKey="contractId"
            loading={activeFrame.loading}
            dataSource={activeFrame.rows}
            pagination={{ pageSize: 8 }}
            columns={[
              { title: "Client", dataIndex: "clientName", key: "clientName" },
              {
                title: "End Date",
                dataIndex: "endDate",
                key: "endDate",
                render: (v) => moment(v).format("DD MMM YYYY"),
              },
              {
                title: "Value",
                key: "amount",
                render: (_, r) => `${r.currency} ${r.amount?.toLocaleString()}`,
              },
              {
                title: "Outcome",
                dataIndex: "outcome",
                key: "outcome",
                render: (v) => {
                  const color = v === "renewed" ? "green" : v === "lost" ? "red" : "default";
                  // "pending" (still due, not yet decided) reads as "running"
                  // here — the contract's current term is still in progress.
                  const label = v === "pending" ? "running" : v;
                  return <Tag color={color}>{label}</Tag>;
                },
              },
            ]}
          />
        ) : activeFrame?.kind === "team" ? (
          <Table
            rowKey="staffId"
            loading={activeFrame.loading}
            dataSource={activeFrame.rows}
            pagination={{ pageSize: 8 }}
            scroll={{ x: "max-content" }}
            columns={[
              { title: activeFrame.role === "bh" ? "Brand Strategist" : "Brand Manager", dataIndex: "name", key: "name" },
              { title: "Due for Renewal", dataIndex: "dueCount", key: "dueCount" },
              { title: "Renewed", dataIndex: "renewed", key: "renewed", render: (v) => <Tag color="green">{v}</Tag> },
              { title: "Lost", dataIndex: "lost", key: "lost", render: (v) => <Tag color="red">{v}</Tag> },
              { title: "Upcoming Contracts", dataIndex: "upcomingCount", key: "upcomingCount", render: (v) => <Tag>{v}</Tag> },
              {
                title: "Renewal Rate",
                dataIndex: "renewalRate",
                key: "renewalRate",
                render: (v) => (v == null ? "-" : `${v}%`),
              },
              {
                title: "",
                key: "action",
                width: 210,
                render: (_, row) => (
                  <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                    <a onClick={() => drillIntoRow(row)}>{row.role === "bm" ? "View clients" : "View team"}</a>
                    <Dropdown
                      menu={{
                        items: [
                          { key: "excel", label: "Excel (.xlsx)", icon: <FileExcelOutlined /> },
                          { key: "pdf", label: "PDF (.pdf)", icon: <FilePdfOutlined /> },
                        ],
                        onClick: ({ key }) => exportForBM(row, key),
                      }}
                    >
                      <Button size="small" icon={<DownloadOutlined />} loading={exportingKey === row.staffId}>
                        Download
                      </Button>
                    </Dropdown>
                  </div>
                ),
              },
            ]}
          />
        ) : null}
      </Modal>
    </>
  );
}

export default RenewalRate;
